<template>
    <PageWrapper :breadcrumb-links="breadcrumbLinks">
        <main
            class="mx-auto w-full max-w-7xl px-2 lg:pl-[calc(theme(spacing.64)+theme(spacing.4))]"
        >
            <StickyPanel
                :top-default="0"
                :top-responsive="10"
            >
                <FilterToolbar
                    :page-data="pageData"
                    :buttons="toolbarConfig.buttons"
                />
                <OrderBar
                    :current="pageData.sortBy"
                    :order-options="pageData.sortOptions"
                />
            </StickyPanel>
            <AlertBox
                v-if="pageData.data.items.length === 0 && pageData.loading === false"
                state="warning"
                :icon="MdiInformation"
                :title="t('Tags')"
            >
                <p class="py-1 text-sm">{{ t('notFoundText') }}</p>
            </AlertBox>
            <div
                v-if="pageData.data.items.length > 0"
                :key="`page-data-list-${pageDataListKey}`"
                class="divide-y divide-gray-200"
            >
                <div
                    v-for="tag in pageData.data.items"
                    :key="tag.tagId"
                    class="group/template"
                >
                    <router-link
                        :to="{ name: 'tag', params: { tagid: tag.tagId } }"
                        class="flex items-center justify-start gap-4 py-2 transition duration-150 ease-in-out hover:bg-gray-100"
                    >
                        <div class="flex-none px-2">
                            <MdiTag
                                class="h-7 w-7 text-blue-400"
                                :style="`color: ${tag.color}`"
                            />
                        </div>
                        <div class="flex grow flex-col gap-2">
                            <div class="flex items-center">
                                <TagShow :tags="[tag]" />
                            </div>
                            <p class="font-base truncate text-sm text-wrap text-gray-500">
                                {{ tag.description }}
                            </p>
                        </div>
                        <div class="hidden md:flex md:flex-col md:items-end md:justify-start">
                            <AuditInfo :item="tag" />
                        </div>
                        <div class="flex-none px-2">
                            <MdiChevronRight class="h-5 w-5 text-gray-400" />
                        </div>
                    </router-link>
                </div>
            </div>
        </main>
    </PageWrapper>
    <div
        class="fixed right-0 bottom-0 left-1 z-2000 flex h-16 items-center justify-center bg-white lg:left-64"
    >
        <Pager
            :key="pager.key"
            :current="pager.current"
            :end="pager.end"
        />
    </div>
    <Spinner
        v-if="pageData.loading === true"
        type="dots"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="t('loading')"
        :inline="false"
        :pulse="true"
    />
</template>
<script setup>
import { reactive, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { listTags } from '../composables/useTags';
import TagShow from '../ui/TagShow.vue';
import Spinner from '../ui/Spinner.vue';
import AlertBox from '../ui/AlertBox.vue';
import { t } from '../composables/i18n';
import { reloadPageData } from '../composables/usePages';
import { parseSortBy, sortByChanged } from '../composables/useSortBy';
import Pager from '../ui/Pager.vue';
import { makePager } from '../composables/usePaginator';
import PageWrapper from '../ui/PageWrapper.vue';
import FilterToolbar from '../ui/FilterToolbar.vue';
import OrderBar from '../ui/OrderBar.vue';
import AuditInfo from '../ui/AuditInfo.vue';
import { getRndKey, composeBackUrl } from '../composables/utils';
import { MdiChevronRight, MdiTag, MdiInformation, MdiPlus } from 'materialdesignicons-vue3/icons/';
import StickyPanel from '../ui/StickyPanel.vue';

const route = useRoute();
const router = useRouter();
const sortDefault = ['name:asc'];
const pageDataListKey = ref(getRndKey(17));
const pageData = reactive({
    data: {
        items: [],
        totalCount: 0,
    },
    currentPage: parseInt(route.params.page) || 1,
    perPage: 15,
    search: route.query.search || '',
    type: route.query.type || '',
    loading: true,
    useTags: false,
    tags: {
        items: [],
        totalCount: 0,
    },
    selectedTags: [],
    sortDefault,
    sortBy: parseSortBy(route.query.sortBy, sortDefault),
    filters: {},
    sortOptions: [
        /* { name: 'Id', value: 'id' }, */
        { name: t('sortName'), value: 'name' },
        { name: t('sortCreated'), value: 'created' },
        { name: t('sortUpdated'), value: 'updated' },
    ],
});
const pager = reactive(makePager(pageData.currentPage, pageData.data.totalCount, 4));
const fromUrl = ref(composeBackUrl(router, route));
const breadcrumbLinks = reactive([
    {
        name: 'dashboard',
        title: t('Dashboard'),
    },
    {
        ...fromUrl.value,
        title: t('Tags'),
    },
    {
        name: 'tags',
        title: t('Tags'),
        final: true,
    },
]);

const toolbarConfig = reactive({
    buttons: [
        {
            action: () => {
                router.push({
                    name: 'tag',
                    params: {
                        tagid: '0',
                    },
                    query: {
                        from: fromUrl.value,
                    },
                });
            },
            show: true,
            title: t('buttonNewTag'),
            icon: MdiPlus,
        },
    ],
});

const refreshPageData = async () => {
    await reloadPageData(pageData, listTags, pager);
    breadcrumbLinks[1].title = `${t('Tags')} (${pageData.data.totalCount})`;
    pageDataListKey.value = getRndKey(17);
    fromUrl.value = composeBackUrl(router, route);
};

watch(
    () => route.params.page,
    async (newPage, oldPage) => {
        if (newPage !== oldPage) {
            //console.log('Route changed', newPage, oldPage);
            pageData.currentPage = parseInt(newPage) || 1;
            await refreshPageData();
        }
    },
);

watch(
    () => route.query,
    async (newQuery, oldQuery) => {
        const filterKeys = Object.keys(pageData.filters);
        const searchChanged = newQuery.search !== oldQuery.search;
        const filtersChanged = filterKeys.some((key) => newQuery[key] !== oldQuery[key]);
        const sortChanged = sortByChanged(newQuery, oldQuery);
        if (searchChanged || filtersChanged || sortChanged) {
            //console.log('Query changed', newQuery, oldQuery);
            pageData.search = newQuery.search || '';
            for (const key of filterKeys) {
                pageData.filters[key].active = newQuery[key] || '';
            }
            if (sortChanged) {
                pageData.sortBy = parseSortBy(newQuery.sortBy, pageData.sortDefault);
            }
            pageData.currentPage = 1;
            await refreshPageData();
        }
    },
);

onMounted(async () => {
    await refreshPageData();
});
</script>
